import * as checkSvg from '!raw-loader!../../../assets/img/check-solid.svg';
import * as newCallSvg from '!raw-loader!../../../assets/img/phone-alt-solid.svg';
import * as notificationSvg from '!raw-loader!../../../assets/img/sam.plain.svg';
import * as cancelSvg from '!raw-loader!../../../assets/img/times-circle-solid.svg';
import { Injectable } from '@angular/core';
import { SwPush } from '@angular/service-worker';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { SVG } from '@svgdotjs/svg.js';
import Canvg, { presets } from 'canvg';
import _ from 'lodash';
import * as rasterizeHTML from 'rasterizehtml';
import { combineLatest, from, iif, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { UserStatusEnum } from 'src/app/samwebDescription/samwin/UserStatusEnum';
import { AvatarService } from 'src/app/services/avatar/avatar.service';
import { ServiceWorkerStaticValues } from 'src/app/services/service-workers/serviceWorkerStaticValues';
import {
   NativeNotificationAddRequestToClose,
   NativeNotificationAddRequestToOpen,
   NativeNotificationAddTemplate,
   NativeNotificationCloseByKey,
   NativeNotificationFinishClose,
   NativeNotificationRemoveTemplate,
   NativeNotificationSetOpenNotifications,
} from 'src/app/store/notification/notification-native/notification-native.actions';
import { NotificationContent, NotificationNativeState } from 'src/app/store/notification/notification-native/notification-native.state';
import {
   AcceptNewOutboundCallRequest,
   CloseOpenConfirmOutboundCallNotifications,
} from 'src/app/store/notification/notification-toastr/notification-toastr.actions';
import { SamwinUnreachableWarningSelectors } from 'src/app/store/notification/samwinUnreachableWarning/samwin-unreachable-warning.selectors';
import { CallTagsSelectors } from 'src/app/store/samwin/call-tags/call-tags.selectors';
import { OpenChat } from 'src/app/store/samwin/chats/chats.actions';
import { ChatsSelectors } from 'src/app/store/samwin/chats/chats.selectors';
import { AcceptRingingCallAction, EndCallAction } from 'src/app/store/samwin/client-phone/client-phone.actions';
import { ClientPhoneState } from 'src/app/store/samwin/client-phone/client-phone.state';
import { SamwinLinesStateSelector } from 'src/app/store/samwin/samwin-lines/samwin-lines.selector';
import { OpenMessage } from 'src/app/store/samwin/samwin-mail-list/samwin-message-list.actions';
import { SamwinMessageLinesStateSelectors } from 'src/app/store/samwin/samwin-message-lines/samwin-message-lines.selectors';
import { RequiredAgentForSkills, RequiredAgentsInfo } from 'src/app/store/samwin/samwin-skills/required-agents-dto';
import { ChangeSkillStatus } from 'src/app/store/samwin/samwin-skills/samwin-skills.actions';
import { SamwinSkillsState } from 'src/app/store/samwin/samwin-skills/samwin-skills.state';
import { SkillConstants } from 'src/app/store/samwin/samwin-skills/skill-constants';
import { SkillGroup } from 'src/app/store/samwin/samwin-skills/skill-group';
import { RequestUserStatusChange } from 'src/app/store/samwin/samwin-user-status/samwin-user-status.actions';
import { SessionState } from 'src/app/store/session/session.state';
import * as samwinIcon from 'src/assets/img/sam.png';
import { Originator } from 'src/generated/signalR/originator';
import { SkillConfigurationRemoteDto } from 'src/generated/signalR/skill-configuration-remote-dto';
import { DirectoriesService } from 'src/generated/swagger';
import { Colors } from 'src/globalTheme/definitions';
import { Url } from 'url';

@Injectable({
   providedIn: 'root',
})
export class NativeNotificationService {
   private skillIcons: Map<string, Url> = new Map();
   private svgIcons: Map<string, Url> = new Map();

   private basicColor = '#808080';
   private emptyColor = '#FFFFFF';
   private fallbackColor = '#808080';

   private getServiceWorker() {
      return navigator?.serviceWorker;
   }

   private getServiceWorkerRegistration(): Observable<ServiceWorkerRegistration | undefined> {
      if (_.isNil(this.getServiceWorker())) {
         return of(null);
      }
      return from(this.getServiceWorker().getRegistration());
   }

   public requestPermissionForNotification$(): Observable<NotificationPermission> {
      // Let's check if the browser supports notifications
      if (!('Notification' in window)) {
         console.warn('This browser does not support desktop notification');
         return of('denied');
      }
      if (Notification.permission === 'granted') {
         console.log('The application is granted to show notification');
         return of(Notification.permission);
      }
      return from(
         Notification.requestPermission((permission) => {
            console.log(`Request permission result is ${permission}`);
         })
      );
   }

   private isServiceWorkerReady() {
      return from(
         this.getServiceWorker()
            .ready.then((registration) => {
               return registration;
            })
            .catch((error) => {
               console.error('Service Worker is ready state was not retrievable', error);
               return null;
            })
      )
         .pipe(
            distinctUntilChanged(),
            filter((result) => !_.isNil(result)),
            map((serviceWorkerRegistration: ServiceWorkerRegistration) => serviceWorkerRegistration)
         )
         .toPromise();
   }

   private serviceWorkerRegistration() {
      return from(
         this.isServiceWorkerReady()
            .then((registration) => {
               return registration;
            })
            .catch((error) => {
               console.error('Service Worker registration was not retrievable', error);
               return null;
            })
      )
         .pipe(
            distinctUntilChanged(),
            filter((result) => !_.isNil(result)),
            map((serviceWorkerRegistration: ServiceWorkerRegistration) => serviceWorkerRegistration)
         )
         .toPromise();
   }

   private nativeNotifications() {
      return from(
         this.serviceWorkerNotificationRequest()
            .then((notifications) => {
               return notifications;
            })
            .catch((error) => {
               console.error('Native Notifications where not retrievable', error);
               return null;
            })
      )
         .pipe(
            distinctUntilChanged(),
            filter((result) => !_.isNil(result)),
            map((notifications: Notification[]) => notifications)
         )
         .toPromise();
   }

   private serviceWorkerNotificationRequest() {
      return from(
         this.serviceWorkerRegistration()
            .then((registration) => {
               return registration?.getNotifications();
            })
            .catch((error) => {
               console.error('Native Notifications request failed', error);
               return null;
            })
      )
         .pipe(
            distinctUntilChanged(),
            filter((result) => !_.isNil(result)),
            map((notifications: Notification[]) => notifications)
         )
         .toPromise();
   }

   private currentNotificationsByKey(tag: string, id: number) {
      return from(
         this.nativeNotifications()
            .then((notifications) => {
               return notifications.filter((notification) => notification.tag === tag && notification.data === id);
            })
            .catch((error) => {
               console.error('Native Notification could not be found due to request error', error);
               return null;
            })
      )
         .pipe(
            distinctUntilChanged(),
            filter((result) => !_.isNil(result)),
            map((notifications: Notification[]) => _.head(notifications))
         )
         .toPromise();
   }

   private requestAllNotificationWhenReady() {
      return this.nativeNotifications().then((notifications) => {
         this.store.dispatch(new NativeNotificationSetOpenNotifications(notifications));
      });
   }

   private isNotificationOpen(tag: string, id: number) {
      return this.currentNotificationsByKey(tag, id)
         .then((notification) => {
            return !_.isNil(notification) ? true : false;
         })
         .catch((error) => {
            console.error('Native Notification open state could not be requested', error);
            return false;
         });
   }

   private isNotificationOpen$(tag: string, id: number) {
      return from(this.isNotificationOpen(tag, id).then((result) => result));
   }

   private closeNotification(tag: string, id: number) {
      // NOTE: Wait for state switch to false, otherwise second notification will stay open when going from hidden to visible.
      return from(
         this.isNotificationOpen(tag, id)
            .then((isOpen) =>
               isOpen
                  ? this.currentNotificationsByKey(tag, id)
                       .then((notification) =>
                          of(notification)
                             .pipe(
                                tap((not) => {
                                   not.close();
                                })
                             )
                             .toPromise()
                             .then(() => true)
                             .catch(() => false)
                       )
                       .catch(() => false)
                  : false
            )
            .catch(() => false)
      )
         .pipe(filter((isOpen) => !isOpen))
         .toPromise();
   }

   private closeNotificationWhenReady(tag: string, id: number) {
      return this.closeNotification(tag, id)
         .then(() => {
            return { tag: tag, id: id };
         })
         .catch((error) => {
            console.error('Request to Close Native Notification by key failed', tag, id, '==>', error);
            return null;
         });
   }

   private currentRegistration$() {
      return from(this.serviceWorkerRegistration()).pipe(filter((registration) => !_.isNil(registration)));
   }

   private currentNotifications$() {
      return this.currentRegistration$().pipe(switchMap((registration: ServiceWorkerRegistration) => from(registration?.getNotifications())));
   }

   private closeNotificationWhenReady$(tag: string, id: number) {
      return from(this.closeNotificationWhenReady(tag, id)).pipe(filter((key) => !_.isNil(key)));
   }

   public closeNotificationWithTag$(tag: string, notificationId: number): Observable<any> {
      return this.store.dispatch(new NativeNotificationAddRequestToClose({ tag: tag, id: notificationId }));
   }

   private handleWorkerMessageEvent(workerMessageEvent: MessageEvent) {
      if (typeof workerMessageEvent.data === 'string') {
         switch (workerMessageEvent.data) {
            case ServiceWorkerStaticValues.NATIVE_NOTIFICATION_CLOSED:
               this.requestAllNotificationWhenReady();
               break;
            case ServiceWorkerStaticValues.NATIVE_NOTIFICATION_CLICKED:
               this.requestAllNotificationWhenReady();
               break;
            default:
               console.warn('No event handler for worker MessageEvent', workerMessageEvent);
         }
      }
   }

   private handleNotificationEvent(action: string, notificationId: number) {
      switch (action) {
         case ServiceWorkerStaticValues.ACCEPT_CALL_NOTIFICATION_ACTION:
            this.store.dispatch(new AcceptRingingCallAction());
            break;
         case ServiceWorkerStaticValues.DECLINE_CALL_NOTIFICATION_ACTION:
            this.store.dispatch(new EndCallAction());
            break;
         case ServiceWorkerStaticValues.ACCEPT_NEW_OUTBOUND_CALL_REQUEST_ACTION:
            this.store.dispatch(new AcceptNewOutboundCallRequest(notificationId));
            this.store.dispatch(new CloseOpenConfirmOutboundCallNotifications(notificationId));
            break;
         case ServiceWorkerStaticValues.CANCEL_NEW_OUTBOUND_CALL_REQUEST_ACTION:
            this.store.dispatch(new CloseOpenConfirmOutboundCallNotifications(notificationId));
            break;
         case ServiceWorkerStaticValues.OPEN_MESSAGE_NOTIFICATION_ACTION:
            const currentAssignedMessage = this.store.selectSnapshot(SamwinMessageLinesStateSelectors.getCurrentAssignedMessage);
            this.store.dispatch(new OpenMessage(currentAssignedMessage.callId, currentAssignedMessage.messageId));
            break;
         case ServiceWorkerStaticValues.OPEN_CHAT_NOTIFICATION_ACTION:
            const ringingChatId = this.store.selectSnapshot(ChatsSelectors.getRingingChatId);
            this.store.dispatch(new OpenChat(ringingChatId));
            break;
         case ServiceWorkerStaticValues.RESOLVE_UNREACHABLE_NOTIFICATION_ACTION:
            if (this.store.selectSnapshot(SamwinUnreachableWarningSelectors.isUnreachableBecauseOfUserstate))
               this.store.dispatch(new RequestUserStatusChange(UserStatusEnum.AVAILABLE.value, Originator.User));
            if (this.store.selectSnapshot(SamwinUnreachableWarningSelectors.isUnreachableBecauseOfSkills))
               this.store.dispatch(new ChangeSkillStatus(SkillConstants.OVERALL_SKILL_ID, true, null));
            break;
         case ServiceWorkerStaticValues.DO_NOTHING_ACTION:
            console.debug('DO_NOTHING_ACTION requires no handling');
            break;
         default:
            console.warn('No event handler for notification:', action);
      }
      this.requestAllNotificationWhenReady();
   }

   public createColorizedNotificationPng$(color: string): Observable<any> {
      // Note: check if image is cached to improve speed of all followup notifications with same color.
      const imageUrl = this.skillIcons.get(color);
      if (!_.isNil(imageUrl)) {
         return of(imageUrl);
      }
      const draw = SVG('#drawingSvg');
      draw.clear();
      draw.svg(notificationSvg.default);
      // @ts-ignore
      draw.find('#path3691').fill(color);
      const coloredSvg = draw.svg();
      const canvas = new OffscreenCanvas(100, 100);
      const ctx = canvas.getContext('2d');
      return of(Canvg.fromString(ctx, coloredSvg, presets.offscreen())).pipe(
         take(1),
         switchMap((v) => v.render()),
         take(1),
         // NOTE: seems like it does not return a blob the second time.
         switchMap(() => canvas.convertToBlob()),
         map((x) => window.URL.createObjectURL(x)),
         tap((x) => this.skillIcons.set(color, x))
      );
   }

   public createColorizedNotificationActionPngFromSvg$(svg: string, color: string): Observable<any> {
      // Note: check if image is cached to improve speed of all followup notifications with same color.
      const imageUrl = this.svgIcons.get(`${svg}-${color}`);
      if (!_.isNil(imageUrl)) {
         return of(imageUrl);
      }
      const draw = SVG(document.createElement('div'));
      draw.svg(svg);
      // @ts-ignore
      draw.find('path').fill(color);

      const canvas = new OffscreenCanvas(32, 32);
      const ctx = canvas.getContext('2d');
      const coloredSvg = draw.svg();

      return of(Canvg.fromString(ctx, coloredSvg, presets.offscreen())).pipe(
         take(1),
         switchMap((v) => v.render()),
         take(1),
         switchMap(() => canvas.convertToBlob()),
         map((x) => window.URL.createObjectURL(x)),
         tap((x) => this.svgIcons.set(`${svg}-${color}`, x))
      );
   }

   public getNotificationHtml(width: number, height: number): Observable<string> {
      const canvas: HTMLCanvasElement = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const callTag$ = this.store.select(CallTagsSelectors.isTagAvailableForCurrentCall).pipe(
         distinctUntilChanged((isTagAvailable) => isTagAvailable),
         filter((isTagAvailable) => isTagAvailable),
         switchMap(() => this.store.select(CallTagsSelectors.getCallTagFromActiveOrRingingCall).pipe(map((getCallTag) => getCallTag('NOTIFICATION_HTML'))))
      );

      return callTag$.pipe(
         switchMap((callTag) => {
            if (!callTag.allReplacedTagsFound || _.isEmpty(callTag?.text)) {
               return of('');
            }
            return from(rasterizeHTML.drawHTML(callTag.text, canvas)).pipe(map(() => canvas.toDataURL('image/png')));
         })
      );
   }

   public createNativeNotification$(tag: string, notificationId: number): Observable<any> {
      if (tag === ServiceWorkerStaticValues.AWAITING_CALL_NOTIFICATION_TAG) {
         return this.updateAwaitingCallContent$(notificationId);
      }
      if (tag === ServiceWorkerStaticValues.AWAITING_CHAT_NOTIFICATION_TAG) {
         return this.updateChatContent$(notificationId);
      }
      if (tag === ServiceWorkerStaticValues.ASSIGNED_MESSAGE_NOTIFICATION_TAG) {
         return this.updateAssignedMailContent$(notificationId);
      }
      if (tag === ServiceWorkerStaticValues.UNREACHABLE_NOTIFICATION_TAG) {
         return this.updateUnreachableContent$(notificationId);
      }
      if (tag === ServiceWorkerStaticValues.REQUIRE_AGENTS_NOTIFICATION_TAG) {
         return this.updateRequireMoreAgentsContent$(notificationId);
      }
      if (tag === ServiceWorkerStaticValues.WAITING_ITEM_NOTIFICATION_TAG) {
         return this.updateNewWaitingItemContent$(notificationId);
      }

      return of(false);
   }

   public createNativeNotificationWithParameter$(tag: string, notificationId: number, parameter: string): Observable<any> {
      if (tag === ServiceWorkerStaticValues.NEW_OUTBOUND_CALL_REQUEST_TAG) {
         return this.updateOutboundCallContent$(notificationId, parameter);
      }
      return of(false);
   }

   public createNewNotificationFromTemplate$(content: NotificationContent): Observable<any> {
      if (_.isNil(this.getServiceWorker())) {
         console.warn('No service worker available on this browser!');
         return of(null);
      }
      return combineLatest([this.getServiceWorkerRegistration(), of(content)]).pipe(
         take(1),
         switchMap(([serviceWorkerRegistration, notificationContent]) => {
            if (_.isNil(serviceWorkerRegistration)) {
               console.warn('No ServiceWorker available to display native notification');
               return of(null);
            }
            return this.requestPermissionForNotification$().pipe(
               filter((x) => x === 'granted'),
               filter((x) => !_.isNil(serviceWorkerRegistration)),
               switchMap((x) => this.showNotificationFromTemplate(notificationContent))
            );
         })
      );
   }

   public showNotificationFromTemplate(content: NotificationContent) {
      return this.getServiceWorker().ready.then((registration) => {
         registration
            .showNotification(content.title, content.content)
            .then(() =>
               this.store.dispatch(
                  new NativeNotificationAddRequestToOpen({
                     tag: content?.content?.tag ?? ServiceWorkerStaticValues.DO_NOTHING_ACTION,
                     id: content?.content?.data,
                  })
               )
            )
            .then(() =>
               this.store.dispatch(
                  new NativeNotificationRemoveTemplate({
                     tag: content?.content?.tag ?? ServiceWorkerStaticValues.DO_NOTHING_ACTION,
                     id: content?.content?.data,
                  })
               )
            );
      });
   }

   public displayNotification(title: string, message: string) {
      const content: NotificationContent = {
         title: title,
         content: {
            body: message,
            icon: samwinIcon.default,
            requireInteraction: true,
            renotify: false,
         },
      };
      return this.showNotificationFromTemplate(content);
   }

   public addContentToTemplates(content: NotificationContent) {
      if (_.isUndefined(content?.content?.tag)) {
         console.error('Native notifications without tags are not allowed to be created!');
         return;
      }
      this.store.dispatch(new NativeNotificationAddTemplate({ tag: content.content.tag, id: content.content.data }, content));
   }

   constructor(
      private swPush: SwPush,
      private store: Store,
      private translationService: TranslateService,
      private directoriesService: DirectoriesService,
      private avatarService: AvatarService
   ) {
      if (_.isNil(this.getServiceWorker())) {
         console.warn('No service worker available on this browser!');
         return;
      }
      this.getServiceWorker().onmessage = (workerMessageEvent: MessageEvent) => {
         this.handleWorkerMessageEvent(workerMessageEvent);
      };
      this.swPush.notificationClicks.subscribe((x: { action: string; notification: NotificationOptions & { title: string } }) => {
         console.log('Notification clicked event received', x.action);
         this.handleNotificationEvent(x.action, x.notification.data);
      });

      setTimeout(() => {
         this.store
            .select(SessionState.getIsAuthenticated)
            .pipe(
               distinctUntilChanged(),
               filter((isAuthenticated) => isAuthenticated === true),
               switchMap(() => this.requestPermissionForNotification$())
            )
            .subscribe();
      });

      this.store
         .select(NotificationNativeState.createAbleNotifications)
         .pipe(
            filter((notifications) => !_.isEmpty(notifications)),
            map((notifications) => _.head(notifications)),
            switchMap((notification) => this.createNewNotificationFromTemplate$(notification.notificationContent))
         )
         .subscribe();

      this.store
         .select(NotificationNativeState.notCreateAbleNotificationKeys)
         .pipe(
            filter((keys) => !_.isEmpty(keys)),
            tap((keys) => this.store.dispatch(new NativeNotificationRemoveTemplate(keys)))
         )
         .subscribe();

      combineLatest([this.store.select(NotificationNativeState.isWaitingToOpen)])
         .pipe(
            switchMap(([isWaitingToOpen]) =>
               iif(
                  () => isWaitingToOpen,
                  this.currentNotifications$().pipe(tap((notifications) => this.store.dispatch(new NativeNotificationSetOpenNotifications(notifications)))),
                  of(null)
               )
            )
         )
         .subscribe();

      this.store
         .select(NotificationNativeState.closableKeys)
         .pipe(
            tap((tags) => {
               if (!_.isEmpty(tags)) {
                  this.store.dispatch(new NativeNotificationCloseByKey(tags));
               }
            })
         )
         .subscribe();

      this.store
         .select(NotificationNativeState.closedNotificationKeys)
         .pipe(
            filter((keys) => !_.isEmpty(keys)),
            map((keys) => _.head(keys)),
            switchMap((key) => this.closeNotificationWhenReady$(key?.tag, key?.id)),
            switchMap((key) =>
               this.isNotificationOpen$(key?.tag, key?.id).pipe(
                  switchMap((isOpen) => iif(() => !isOpen, this.store.dispatch(new NativeNotificationFinishClose({ tag: key?.tag, id: key?.id })), of(null)))
               )
            )
         )
         .subscribe();
   }

   private updateAssignedMailContent$(notificationId: number): Observable<NotificationContent> {
      return this.store.select(SamwinMessageLinesStateSelectors.getCurrentAssignedMessage).pipe(
         distinctUntilChanged(null, (e) => e?.messageId),
         switchMap((message) =>
            this.createColorizedNotificationPng$(
               message?.skill?.skills.length > 1 ? this.basicColor : message?.skill?.skillConfiguration?.skillColor ?? this.emptyColor
            ).pipe(
               take(1),
               map((iconColor) => {
                  if (_.isNil(message)) {
                     return { title: '', content: {} };
                  }
                  let actions = new Array<NotificationAction>();
                  actions = actions.concat({
                     title: this.translationService.instant('notification.native_notifications.assigned_mail.open'),
                     action: ServiceWorkerStaticValues.OPEN_MESSAGE_NOTIFICATION_ACTION,
                  });

                  const result: NotificationContent = {
                     title: message.address,
                     content: {
                        tag: ServiceWorkerStaticValues.ASSIGNED_MESSAGE_NOTIFICATION_TAG,
                        data: notificationId,
                        actions: actions,
                        icon: iconColor,
                        requireInteraction: true,
                        renotify: true,
                        body: message.subject,
                     },
                  };
                  return result;
               })
            )
         ),
         tap((notification) => this.addContentToTemplates(notification))
      );
   }

   private updateChatContent$(notificationId: number): Observable<NotificationContent> {
      return this.store.select(ChatsSelectors.getRingingChat).pipe(
         take(1),
         switchMap((chat) =>
            this.createColorizedNotificationPng$(
               chat?.skill?.skills.length > 1 ? this.basicColor : chat?.skill?.skillConfiguration?.skillColor ?? this.emptyColor
            ).pipe(
               map((iconColor) => {
                  if (_.isNil(chat)) {
                     return { title: '', content: {} };
                  }
                  let actions = new Array<NotificationAction>();
                  actions = actions.concat({
                     title: this.translationService.instant('notification.native_notifications.new_chat.open'),
                     action: ServiceWorkerStaticValues.OPEN_CHAT_NOTIFICATION_ACTION,
                  });

                  const result: NotificationContent = {
                     title: chat.peer.name,
                     content: {
                        tag: ServiceWorkerStaticValues.AWAITING_CHAT_NOTIFICATION_TAG,
                        data: notificationId,
                        actions: actions,
                        icon: iconColor,
                        requireInteraction: true,
                        renotify: true,
                        body: chat.peer.email,
                     },
                  };
                  return result;
               })
            )
         ),
         tap((notification) => this.addContentToTemplates(notification))
      );
   }

   private updateUnreachableContent$(notificationId: number): Observable<NotificationContent> {
      return combineLatest([
         this.store.select(SamwinUnreachableWarningSelectors.isUnreachableBecauseOfUserstate),
         this.store.select(SamwinUnreachableWarningSelectors.isUnreachableBecauseOfSkills),
         this.store.select(SamwinUnreachableWarningSelectors.isUnreachableBecauseOfDeactivated),
         this.createColorizedNotificationActionPngFromSvg$(checkSvg.default, Colors.SAM_GREEN),
      ]).pipe(
         take(1),
         map(([isUnreachableBecauseOfUserstate, isUnreachableBecauseOfSkills, isUnreachableBecauseOfAgent, resolveIcon]) => {
            const linebreak = '\n';
            let reasonString = '';

            if (isUnreachableBecauseOfUserstate) {
               reasonString = `${reasonString}${linebreak} - ` + this.translationService.instant('notification.reachability.because_of_userstatus');
            }
            if (isUnreachableBecauseOfSkills) {
               reasonString =
                  `${reasonString}${linebreak} - ` + this.translationService.instant('notification.reachability.because_not_logged_on_to_any_skill');
            }
            if (isUnreachableBecauseOfAgent) {
               reasonString = `${reasonString}${linebreak} - ` + this.translationService.instant('notification.reachability.because_of_deactivated');
            }
            const messageTitle = this.translationService.instant('notification.reachability.title_native');
            const messageBody = this.translationService.instant('notification.reachability.description_native', { reasons: reasonString });
            const buttontext = this.translationService.instant('notification.reachability.resolve_button_Text');

            const actions = new Array<NotificationAction>();
            if (isUnreachableBecauseOfUserstate || isUnreachableBecauseOfSkills) {
               actions.push({
                  title: buttontext,
                  action: ServiceWorkerStaticValues.RESOLVE_UNREACHABLE_NOTIFICATION_ACTION,
                  icon: resolveIcon,
               });
            }
            const result: NotificationContent = {
               title: messageTitle,
               content: {
                  tag: ServiceWorkerStaticValues.UNREACHABLE_NOTIFICATION_TAG,
                  data: notificationId,
                  body: messageBody,
                  icon: samwinIcon.default,
                  actions: actions,
                  requireInteraction: true,
                  renotify: true,
               },
            };
            return result;
         }),
         tap((notification) => this.addContentToTemplates(notification))
      );
   }

   private updateOutboundCallContent$(notificationId: number, numberToDial: string): Observable<NotificationContent> {
      return this.directoriesService.reverseNameLookup(numberToDial).pipe(
         switchMap((nameLookupResponse) => {
            const subscriberName = _.trim(nameLookupResponse.name);
            return combineLatest([
               this.translationService.get('notification.request_new_call.message', { numberToDial: numberToDial }),
               this.translationService.get('notification.request_new_call.title', {
                  contact: _.isEmpty(subscriberName) ? numberToDial : subscriberName,
               }),
               this.translationService.get('notification.request_new_call.actions.dial'),
               this.translationService.get('notification.request_new_call.actions.cancel'),
               this.createColorizedNotificationActionPngFromSvg$(newCallSvg.default, Colors.SAM_GREEN),
               this.createColorizedNotificationActionPngFromSvg$(cancelSvg.default, Colors.SAM_RED),
               this.avatarService.getUserAvatar(numberToDial).pipe(catchError(() => of(null))),
            ]).pipe(
               take(1),
               map(([messageBody, messageTitle, initiateCallLabel, cancelLabel, initiateCallIcon, cancelIcon, subscriberImage]) => {
                  const actions = new Array<NotificationAction>();
                  actions.push({
                     title: initiateCallLabel,
                     action: ServiceWorkerStaticValues.ACCEPT_NEW_OUTBOUND_CALL_REQUEST_ACTION,
                     icon: initiateCallIcon,
                  });
                  actions.push({
                     title: cancelLabel,
                     action: ServiceWorkerStaticValues.CANCEL_NEW_OUTBOUND_CALL_REQUEST_ACTION,
                     icon: cancelIcon,
                  });

                  const result: NotificationContent = {
                     title: messageTitle,
                     content: {
                        tag: ServiceWorkerStaticValues.NEW_OUTBOUND_CALL_REQUEST_TAG,
                        data: notificationId,
                        actions: actions,
                        requireInteraction: true,
                        body: messageBody,
                        renotify: true,
                        icon: _.isNil(subscriberImage) ? samwinIcon.default : window.URL.createObjectURL(subscriberImage),
                     },
                  };
                  return result;
               }),
               tap((notification) => this.addContentToTemplates(notification))
            );
         })
      );
   }

   private updateAwaitingCallContent$(notificationId: number) {
      return this.store.select(SamwinLinesStateSelector.getCurrentCallShortInfo).pipe(
         filter((currentCallShortInfo) => !_.isNil(currentCallShortInfo)),
         switchMap((currentCallShortInfo) =>
            combineLatest([
               this.translationService.get('notification.native_notifications.new_call.body', {
                  callerName: currentCallShortInfo.peerName,
                  callerNumber: currentCallShortInfo.peerId,
               }),
               this.translationService.get('notification.native_notifications.new_call.title', {
                  caller: _.isEmpty(currentCallShortInfo.peerName) ? currentCallShortInfo.peerId : currentCallShortInfo.peerName,
               }),
               this.translationService.get('notification.native_notifications.new_call.accept_call_label'),
               this.translationService.get('notification.native_notifications.new_call.decline_call_label'),
               this.store.select(SamwinSkillsState.getSkillConfigurationForCall).pipe(
                  map((x) => x(currentCallShortInfo.callId)),
                  filter((x) => !_.isNil(x)),
                  switchMap((x: SkillConfigurationRemoteDto) => this.createColorizedNotificationPng$(x.skillColor ?? this.fallbackColor))
               ),
               this.store.select(ClientPhoneState.isCallRingingAndAcceptable),
               this.store.select(SamwinLinesStateSelector.canDisconnectLineA),
               this.getNotificationHtml(1440, 960),
            ]).pipe(
               take(1),
               map(
                  ([
                     messageBody,
                     messageTitle,
                     acceptCallLabel,
                     declineCallLabel,
                     colorizedNotificationPngUrl,
                     isCallRingingAndAcceptable,
                     canDeclineCall,
                     htmlImage,
                  ]) => {
                     const actions = new Array<NotificationAction>();
                     if (isCallRingingAndAcceptable) {
                        actions.push({
                           title: acceptCallLabel,
                           action: ServiceWorkerStaticValues.ACCEPT_CALL_NOTIFICATION_ACTION,
                        });
                     }
                     if (canDeclineCall) {
                        actions.push({
                           title: declineCallLabel,
                           action: ServiceWorkerStaticValues.DECLINE_CALL_NOTIFICATION_ACTION,
                        });
                     }
                     const result: NotificationContent = {
                        title: messageTitle,
                        content: {
                           tag: ServiceWorkerStaticValues.AWAITING_CALL_NOTIFICATION_TAG,
                           data: notificationId,
                           actions: actions,
                           icon: colorizedNotificationPngUrl,
                           image: htmlImage,
                           requireInteraction: true,
                           renotify: true,
                           body: messageBody,
                        },
                     };
                     return result;
                  }
               ),
               tap((notification) => this.addContentToTemplates(notification))
            )
         )
      );
   }

   private updateRequireMoreAgentsContent$(notificationId: number) {
      return this.store.select(SamwinSkillsState.getUnderstaffedSkills(false)).pipe(
         take(1),
         map((skills) => {
            const header: string = this.translationService.instant('notification.required_agents.title_native');

            const message = this.buildRequireMoreAgentsNativeNotificationContent(skills);

            const result: NotificationContent = {
               title: header,
               content: {
                  tag: ServiceWorkerStaticValues.REQUIRE_AGENTS_NOTIFICATION_TAG,
                  data: notificationId,
                  icon: samwinIcon.default,
                  requireInteraction: true,
                  renotify: true,
                  body: message,
               },
            };
            return result;
         }),
         tap((notification) => this.addContentToTemplates(notification))
      );
   }

   private updateNewWaitingItemContent$(notificationId: number): Observable<NotificationContent> {
      return this.store.select(SamwinLinesStateSelector.skillsOfWaitingItems).pipe(
         map((items) => {
            const header: string = this.translationService.instant('notification.waiting_item.title');
            const body = this.buildWaitingItemNativeNotificationContent(items);
            const result: NotificationContent = {
               title: header,
               content: {
                  tag: ServiceWorkerStaticValues.WAITING_ITEM_NOTIFICATION_TAG,
                  data: notificationId,
                  requireInteraction: true,
                  renotify: true,
                  body: body,
               },
            };
            return result;
         }),
         tap((notification) => {
            this.addContentToTemplates(notification);
         })
      );
   }

   private buildWaitingItemNativeNotificationContent(skills: SkillGroup[]) {
      const linebreak = '\n';
      let message = '';
      const skillsToDisplay = skills;
      const uniqueDisplayNames = new Set<string>();

      skillsToDisplay.forEach((skill) => {
         const waitCount = skillsToDisplay.filter((skilll) => skilll.skillId === skill.skillId).length;

         if (!uniqueDisplayNames.has(skill.displayName)) {
            uniqueDisplayNames.add(skill.displayName);

            if (uniqueDisplayNames.size < 4) {
               message += `${this.translationService.instant('notification.waiting_item.new_waiting_items', {
                  skillName: skill.displayName,
                  waitCount: waitCount,
               })}${linebreak}`;
            } else {
               message = `You have waiting items in ${skillsToDisplay.length} skills`;
            }
         }
      });

      return message;
   }

   private buildRequireMoreAgentsNativeNotificationContent(requireAgents: RequiredAgentForSkills[]) {
      // Creates native notification message with 3 states
      // if there is enough space Display Subskills.
      // Else Display skills only
      // If there are to many Skills show the count of the not displayable skills
      const linebreak = '\n';
      let message = '';
      const maxDisplayedLineCount = 4;
      const lineMaxLength = 30;
      const subskills: RequiredAgentsInfo[] = [];
      // fill subskills
      requireAgents
         .filter((skill) => skill.subSkills.length > 1)
         .forEach((skill) => {
            _(skill.subSkills)
               .orderBy((subskill) => subskill.skillName.toLowerCase())
               .forEach((subskill) => {
                  subskills.push(subskill);
               });
         });
      const skillCount: number = requireAgents.length;
      const subSkillCount: number = subskills.length;
      // create booleans to filter which kind of message should be displayed
      const singleSubSkill = skillCount === 1 && subSkillCount > 1;
      const displaySubSkills: boolean = skillCount + subSkillCount <= maxDisplayedLineCount || singleSubSkill;
      const skillOverflow: boolean = skillCount > maxDisplayedLineCount;
      const subSkillOverflow: boolean = singleSubSkill && skillCount + subSkillCount > maxDisplayedLineCount;

      // create message for native notification
      requireAgents.forEach((requireAgent) => {
         const containsSubSkills = requireAgent.subSkills.filter((subskill) => subskill.requiredAgents < requireAgent.requiredAgents).length > 0;
         if (containsSubSkills && displaySubSkills) {
            message = `${message}${linebreak}- `;
            message = `${message}${requireAgent.skillName} `;
            _(requireAgent.subSkills)
               .orderBy((subskill) => subskill.skillName.toLowerCase())
               .forEach((subskill) => {
                  message = `${message}${linebreak}  - `;
                  message = `${message}${subskill.skillName} `;
                  message = `${message} ${this.translationService.instant('notification.required_agents.x_of_y_required_agents_active_native', {
                     activeAgents: subskill.activeAgents,
                     requiredAgents: subskill.requiredAgents,
                  })}`;
               });
         } else {
            message = `${message}${linebreak}- `;
            message = `${message}${requireAgent.skillName} `;
            message = `${message} ${this.translationService.instant('notification.required_agents.x_of_y_required_agents_active_native', {
               activeAgents: requireAgent.activeAgents,
               requiredAgents: requireAgent.requiredAgents,
            })}`;
         }
      });
      // modify message if Skills exceed line count
      if (skillOverflow || subSkillOverflow) {
         const line = message.split(linebreak);
         let i: number;
         let displayedSkills = 0;
         message = '';
         for (i = 1; i <= maxDisplayedLineCount; i++) {
            if (line[i].length > lineMaxLength) {
               i++;
            }
            if (i >= maxDisplayedLineCount) {
               if (subSkillOverflow)
                  message = `${message}  ${this.translationService.instant('notification.required_agents.x_remaining_skills_native', {
                     remainingSkills: subSkillCount - (displayedSkills - skillCount),
                  })}`;
               else
                  message = `${message}${this.translationService.instant('notification.required_agents.x_remaining_skills_native', {
                     remainingSkills: skillCount - displayedSkills,
                  })}`;
            } else {
               message = `${message}${line[i]}${linebreak}`;
               displayedSkills += 1;
            }
         }
      }
      return message;
   }
}
